<template>
  <div class="page">
    <div class="loading-screen" v-if="status=='LOADING'">
        <div>
            <span class="spinner spinner--lg"></span>
            <span>preparing workspace…</span>
        </div>
    </div>

    <div v-if="status=='LOADED'">
        <header class="topbar">
            <div class="topbar__inner">
                <a :href="`/${assesmentId}/questions`" class="btn btn--ghost btn--sm">← Questions</a>
                <span class="brand" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{assesment.name}}</span>
                <div class="topbar__user" v-if="assesment.name!='Sample Exam'">
                    <span class="status-pill status-pill--live">
                        <span class="countdown">{{(getRemainingHours())<10?'0'+(getRemainingHours()):(getRemainingHours())}}:{{(getRemainingMinutes()-1)<10?'0'+(getRemainingMinutes()-1):(getRemainingMinutes()-1)}}:{{(60-currentTimeSeconds)<10?'0'+(60-currentTimeSeconds):(60-currentTimeSeconds)}}</span>
                    </span>
                </div>
                <div class="topbar__user" v-else>
                    <span class="chip">sample — no timer</span>
                </div>
            </div>
        </header>

        <div class="workbench">
            <!-- ===== question panel ===== -->
            <aside class="glass question-panel rise">
                <div class="question-panel__head">
                    <h2 class="card__title">
                        {{question.name}}
                        <span class="badge badge--easy" v-if="question.difficulty=='easy'">easy</span>
                        <span class="badge badge--medium" v-else-if="question.difficulty=='medium'">medium</span>
                        <span class="badge badge--hard" v-else-if="question.difficulty">hard</span>
                    </h2>
                </div>
                <div class="question-panel__scroll">
                    <div class="section-block">
                        <div class="section-block__head">Statement</div>
                        <div class="section-block__body">{{question.statement}}</div>
                    </div>
                    <div class="section-block">
                        <div class="section-block__head">Input Format</div>
                        <div class="section-block__body">
                            <ul>
                                <li v-for="(input, i) in inputFormat.description" :key="i">
                                    <span class="mono" style="color:var(--accent)">{{inputFormat.type[i]}}</span> — {{input}}
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="section-block">
                        <div class="section-block__head">Output Format</div>
                        <div class="section-block__body">
                            <span class="mono" style="color:var(--accent)">{{outputFormat.type}}</span> — {{outputFormat.description}}
                        </div>
                    </div>
                    <div class="section-block">
                        <div class="section-block__head">Constraints</div>
                        <div class="section-block__body">
                            <ul>
                                <li v-for="constraint in question.constraints" :key="constraint" class="mono" style="font-size:0.85rem">{{constraint}}</li>
                            </ul>
                        </div>
                    </div>
                    <div class="section-block">
                        <div class="section-block__head">Sample Test Cases</div>
                        <div class="section-block__body">
                            <dl style="margin:0">
                                <dt class="mono text-dim" style="font-size:0.72rem;text-transform:uppercase;letter-spacing:0.08em">Input</dt>
                                <dd style="margin:2px 0 8px" class="mono" v-for="input in sample.input" :key="'i'+input">{{input}}</dd>
                                <dt class="mono text-dim" style="font-size:0.72rem;text-transform:uppercase;letter-spacing:0.08em">Output</dt>
                                <dd style="margin:2px 0 8px" class="mono" v-for="output in sample.output" :key="'o'+output">{{output}}</dd>
                                <dt class="mono text-dim" style="font-size:0.72rem;text-transform:uppercase;letter-spacing:0.08em">Explanation</dt>
                                <dd style="margin:2px 0 0" v-for="explanation in sample.explanation" :key="'e'+explanation">{{explanation}}</dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </aside>

            <!-- ===== editor panel ===== -->
            <section class="glass editor-panel rise rise-1">
                <div class="editor-toolbar">
                    <span class="editor-dot" style="background:#f87171"></span>
                    <span class="editor-dot" style="background:#fbbf24"></span>
                    <span class="editor-dot" style="background:#34d399"></span>
                    <span class="mono text-dim" style="font-size:0.78rem;margin-left:6px">solution.{{language=='java'?'java':language=='python'?'py':'cpp'}}</span>
                    <div style="flex:1"></div>
                    <button class="theme-dot theme-dot--light" title="Light editor" @click="changeThemeLight"></button>
                    <button class="theme-dot theme-dot--dark" title="Dark editor" @click="changeThemeDark"></button>
                    <select v-model="language" @change="changeLanguage" class="select" style="width:130px;padding:7px 34px 7px 12px;font-size:0.85rem">
                        <option value="java">Java</option>
                        <option value="c_cpp">C++</option>
                        <option value="python">Python</option>
                    </select>
                </div>
                <div id="editor"></div>
                <div class="editor-actions">
                    <button class="btn btn--ghost" @click="runTestCodes" :disabled="runStatus=='LOADING'">
                        <span class="spinner" v-if="runStatus=='LOADING'"></span>
                        <span v-else>▶ Run Test Cases</span>
                    </button>
                    <button class="btn btn--primary" @click="submitCode" :disabled="submitStatus=='LOADING' || runStatus=='LOADING'">
                        <span class="spinner spinner--dark" v-if="submitStatus=='LOADING'"></span>
                        <span v-else>Submit Code ↗</span>
                    </button>
                </div>
            </section>

            <!-- ===== results ===== -->
            <section class="results-panel" v-if="outputStatus" id="output">
                <div class="glass card rise">
                    <div v-if="outputStatus == 'Runtime Error'">
                        <div class="result-card result-card--fail">
                            <div class="result-card__head">✗ Runtime Error</div>
                            <div class="result-card__body"><div class="code-block code-block--error">{{output || 'Your code crashed while running the test cases.'}}</div></div>
                        </div>
                    </div>
                    <div v-else-if="outputStatus == 'Compilation Error'">
                        <div class="result-card result-card--fail">
                            <div class="result-card__head">✗ Compilation Error</div>
                            <div class="result-card__body"><div class="code-block code-block--error">{{output}}</div></div>
                        </div>
                    </div>
                    <div v-else-if="outputStatus=='success'">
                        <div class="score-banner">
                            <span>{{passedTestCases}} / {{question.testCases.input.length}} test cases passed</span>
                            <div class="progress-track"><i :style="{width: (passedTestCases / question.testCases.input.length * 100) + '%'}"></i></div>
                            <span style="font-size:1.3rem">{{passedTestCases == question.testCases.input.length ? '🎉' : '🔎'}}</span>
                        </div>
                        <div v-for="(testCase, i) of testCaseOutput" :key="i"
                             class="result-card" :class="testCase.status == 'passed' ? 'result-card--pass' : 'result-card--fail'">
                            <div class="result-card__head">{{testCase.status == 'passed' ? '✓' : '✗'}} Test Case {{i+1}} — {{testCase.status}}</div>
                            <div class="result-card__body">
                                <dl style="margin:0">
                                    <dt>Input</dt>
                                    <dd>{{question.testCases.input[i].replace(/{|}/gi,"")}}</dd>
                                    <dt>Your Output</dt>
                                    <dd>{{testCase.output}}</dd>
                                    <dt>Expected Output</dt>
                                    <dd>{{expectedOutputArr[i].replace(/{|}/gi,"")}}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
  </div>
  </div>
</template>

<script>
import {codeRunner} from '../services/assesments'
import { getQuestion } from '@/services/questions'
import { getQuestions } from '@/services/questions'
import {submitAnswer} from '../services/answers'
import * as ace from 'brace'
import 'brace/mode/java'
import 'brace/mode/python'
import 'brace/mode/c_cpp'
import 'brace/theme/monokai'
import 'brace/theme/dreamweaver'
import 'brace/snippets/java'
import 'brace/snippets/c_cpp'
import 'brace/snippets/python'
import 'brace/ext/language_tools'
import router from '@/router'
import Vue from 'vue'

let editor;
export default {
    name: 'CodePage',
    data(){
        return {
            status: '',
            runStatus: 'LOADED',
            submitStatus: 'LOADED',
            code: '',
            output: '',
            expectedOutputArr: [],
            testCaseOutput: [],
            passedTestCases: 0,
            outputStatus: '',
            questionId: '',
            assesmentId: '',
            assesment: {},
            assesmentEndTime: {},
            question: {},
            sample: {},
            inputFormat: [],
            outputFormat: {},
            language: 'java',
            currentTimeHour: new Date().getHours(),
            currentTimeMinutes: new Date().getMinutes(),
            currentTimeSeconds: new Date().getSeconds()
        }
    },
    methods: {
        async runTestCodes(){
            if(this.language == 'java')
            this.code = `public class codeRunner {
        ${editor.getValue()}
}`;
            else
            this.code = editor.getValue();
            await this.codeRunner();
            this.$nextTick(() => {
                const el = document.getElementById('output');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        },
        async getAssesment() {
            this.assesmentId = this.$router.currentRoute.params.examId;
            this.status = 'LOADING';
            try {
                const response = await getQuestions(this.assesmentId);
                this.assesment = response.data;
                this.assesmentEndTime = this.assesment.endTime;
            }
            catch (error) {
                this.status = "ERROR";
                this.error = error.message;
            }
        },
        async getCurQuestion(){
            this.questionId = router.history.current.params.questionId;
            this.status = 'LOADING';
            try{
                const response = await getQuestion(this.questionId);
                this.question = response.data;
                this.sample = this.question.sampleTestCases;
                this.inputFormat = this.question.inputFormat;
                this.outputFormat = this.question.outputFormat;
                this.status = 'LOADED';
            }
            catch(error){
                this.status = 'LOADED';
            }
        },
        async codeRunner(){
            this.runStatus = 'LOADING';
            const data = {
                code : this.code,
                testCases: this.question.testCases,
                language: this.language
            };
            const response = await codeRunner(data);
            this.runStatus = 'LOADED';
            this.outputStatus = response.status;
            this.output = response.data;
            this.configureTestCases(response);
        },
        changeLanguage () {
            let inputType = [];
            editor.getSession().setMode(`ace/mode/${this.language}`);
             if (this.language == 'java'){
                    for(let i=0;i<this.inputFormat.type.length;i++){
                   inputType.push(this.inputFormat.type[i] + ` input${i+1} `);
                }
                    editor.getSession().setValue(`public ${this.outputFormat.type} solution (${inputType.toString()}) {
    //write your code here
    return result;
}`);
                }

                else if (this.language == 'python') {
                    for(let i=0;i<this.inputFormat.type.length;i++){
                   inputType.push(` input${i+1}`);
                }
                     editor.getSession().setValue(`def solution (${inputType.toString()}):
    #write your code here
    return result
    `);

                }

                else if (this.language == 'c_cpp') {
                    for(let i=0;i<this.inputFormat.type.length;i++){
                   inputType.push(this.inputFormat.type[i] + ` input${i+1}`);
                }
                     editor.getSession().setValue(`int solution (${inputType.toString()}) {
    //write your code here
    return result;
}
    `);
                }
        },
        changeThemeDark() {
                editor.setTheme('ace/theme/monokai');
        },
        changeThemeLight() {
                editor.setTheme('ace/theme/dreamweaver');
        },
        configureTestCases(response) {
            if(response.status == 'success') {
                const rawOutput = response.data.split(",");
                this.expectedOutputArr = this.question.testCases.expectedOutput.split(",");
                this.testCaseOutput = [];
                this.passedTestCases = 0;
                for(let i=0;i<rawOutput.length-1;i+=2) {
                    let testCase = {
                    };
                    testCase.output = rawOutput[i];
                    if(rawOutput[i+1]=='yes')
                        {
                            testCase.status = 'passed';
                            this.passedTestCases +=1;
                        }
                    else
                        testCase.status = 'failed';
                    this.testCaseOutput.push(testCase);
                }
            }
        },
        async submitCode() {
            await this.runTestCodes();
            this.submitStatus = 'LOADING';
            const data = {
                exam : router.currentRoute.params.examId,
                question: router.currentRoute.params.questionId,
                userId: this.$store.state.auth.userId,
                answer: editor.getValue(),
                testCasesPassed: this.passedTestCases
            }
            try  {
                    await submitAnswer(data);
                    Vue.$toast.success('Answer Submitted Successfully');
                    Vue.$toast.success('Test Cases Passed - '+ this.passedTestCases );
                } catch(error){
                    Vue.$toast.error(error.message);
                }
            this.submitStatus = 'LOADED';
        },
        compareTime() {
            if (this.assesment.name == 'Sample Exam') return;
            if(this.currentTimeHour > this.assesment.endTime.hours )
                this.$router.push({path : '/assesments'});
            else if(this.currentTimeHour == this.assesment.endTime.hours) {
                if(this.currentTimeMinutes >= this.assesment.endTime.minutes)
                    {

                        this.$router.push({path : '/assesments'});
                        Vue.$toast.error('Time Expired');
                    }

            }
        },
        setEditor() {
            editor = ace.edit("editor");
            editor.setTheme('ace/theme/dreamweaver')
            editor.getSession().setMode(`ace/mode/java`);
            editor.setOptions({
                fontSize: "14px",
                enableBasicAutocompletion : true,
                enableLiveAutocompletion: true,
                enableSnippets: true
            });
            let inputType = []
                    for(let i=0;i<this.inputFormat.type.length;i++){
                   inputType.push(this.inputFormat.type[i] + ` input${i+1}`);
                }
            editor.getSession().setValue(`public ${this.outputFormat.type} solution (${inputType.toString()}) {
    //write your code here
    return result;
}`);
        },
        getRemainingMinutes() {
            let minutes = this.assesmentEndTime.minutes-this.currentTimeMinutes;
            return minutes>0?minutes:60+minutes;
        },
        getRemainingHours() {
            let minutes = this.assesmentEndTime.minutes-this.currentTimeMinutes;
            return minutes>0?this.assesmentEndTime.hours-this.currentTimeHour:this.assesmentEndTime.hours-this.currentTimeHour-1;
        }
    },
    async created() {
        await this.getAssesment();
        await this.getCurQuestion();
        this.$nextTick(() => this.setEditor());
        setInterval(() => {
            this.currentTimeHour = new Date().getHours();
            this.currentTimeMinutes = new Date().getMinutes();
            this.currentTimeSeconds = new Date().getSeconds();
            if (this.$router.currentRoute.path != '/assesments')
            this.compareTime();
        },1000);

    }
}
</script>
<style>
.workbench {
    max-width: 1400px;
    margin-inline: auto;
    padding: 24px 22px 64px;
    display: grid;
    grid-template-columns: minmax(340px, 5fr) minmax(420px, 7fr);
    gap: 20px;
    align-items: start;
}
.question-panel { display: flex; flex-direction: column; max-height: calc(100vh - 120px); position: sticky; top: 76px; }
.question-panel__head { padding: 18px 20px 12px; border-bottom: 1px solid var(--border); }
.question-panel__scroll { padding: 16px 18px; overflow-y: auto; }
.editor-panel { overflow: hidden; }
.editor-toolbar {
    display: flex; align-items: center; gap: 7px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--border);
    background: #f8fafc;
}
.editor-dot { width: 11px; height: 11px; border-radius: 50%; display: inline-block; opacity: 0.85; }
.theme-dot {
    width: 20px; height: 20px; border-radius: 50%;
    border: 1px solid var(--border-strong);
    cursor: pointer;
    transition: transform 0.15s ease;
}
.theme-dot:hover { transform: scale(1.15); }
.theme-dot--light { background: #f8fafc; }
.theme-dot--dark { background: #1e1f1c; }
#editor {
    height: 480px;
    font-family: var(--font-mono) !important;
}
.editor-actions {
    display: flex; gap: 12px; justify-content: flex-end;
    padding: 14px 16px;
    border-top: 1px solid var(--border);
    background: #f8fafc;
}
.results-panel { grid-column: 1 / -1; }
@media (max-width: 1020px) {
    .workbench { grid-template-columns: 1fr; }
    .question-panel { position: static; max-height: none; }
    .question-panel__scroll { max-height: 380px; }
}
</style>
